import { Controller } from "react-hook-form";
import { Dispatch, memo, SetStateAction, useCallback, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { DescriptionOfWarehouseItem } from "../../constants/common";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import { useTranslation } from "react-i18next";
import TextInput from "../inputs/TextInput";
import { Button } from "../button";
import { addDescriptionOfWarehouseItemCardSchema } from "../../schemas/addDescriptionOfWarehouseItemCardSchema";
import { APP_ROUTES } from "../../routes";
import { useLocation } from "react-router-dom";

interface AddDescriptionOfWarehouseItemCardProps {
  setIsAddItemFormVisible: React.Dispatch<React.SetStateAction<boolean>>;

  setAddedDescriptionOfWarehouseItems: Dispatch<
    SetStateAction<DescriptionOfWarehouseItem[]>
  >;

  editingItem: DescriptionOfWarehouseItem | null;
  setEditingItem: React.Dispatch<
    React.SetStateAction<DescriptionOfWarehouseItem | null>
  >;

  isFieldsDisabled: boolean;
}

const AddDescriptionOfWarehouseItemCard = ({
  setIsAddItemFormVisible,
  setAddedDescriptionOfWarehouseItems,

  editingItem,
  setEditingItem,

  isFieldsDisabled,
}: AddDescriptionOfWarehouseItemCardProps) => {
  const location = useLocation();
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useFormWithSchema(addDescriptionOfWarehouseItemCardSchema);

  const { t } = useTranslation();

  useEffect(() => {
    if (editingItem) {
      const userData = {
        id: editingItem?.id,
        itemType: editingItem.itemType,
        quantity: editingItem.quantity,
        supplyingExportingEntityForPermitHolder:
          editingItem.supplyingExportingEntityForPermitHolder,
      };

      if (userData) {
        reset(userData);
      }
    }
  }, [editingItem, reset]);

  const onSubmit = useCallback(
    async (data: any) => {
      const details = {
        itemType: data.itemType,
        quantity: data.quantity,
        supplyingExportingEntityForPermitHolder:
          data.supplyingExportingEntityForPermitHolder,
      };
      const id = uuidv4();

      if (editingItem) {
        setAddedDescriptionOfWarehouseItems((prev) =>
          prev.map((visitor) =>
            visitor.id === editingItem.id
              ? {
                  id: editingItem.id,
                  ...details,
                }
              : visitor
          )
        );
        setEditingItem(null);
      } else {
        setAddedDescriptionOfWarehouseItems((prev) => [
          ...prev,
          {
            id,
            ...details,
          },
        ]);
      }

      reset();

      setIsAddItemFormVisible(false);
    },
    [
      editingItem,
      reset,
      setIsAddItemFormVisible,
      setEditingItem,
      setAddedDescriptionOfWarehouseItems,
    ]
  );

  return (
    <>
      <div className="flex flex-col gap-2">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-4">
          <Controller
            name="itemType"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <TextInput
                label={t("itemType")}
                value={value}
                onChange={onChange}
                error={errors?.[name]?.message}
                required
                readOnly={isFieldsDisabled}
              />
            )}
          />

          <Controller
            name="quantity"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <TextInput
                type="number"
                min={1}
                label={t("quantity")}
                value={value?.toString()}
                onChange={onChange}
                error={errors?.[name]?.message}
                required
                readOnly={isFieldsDisabled}
              />
            )}
          />
          <div className="lg:col-span-2">
            <Controller
              name="supplyingExportingEntityForPermitHolder"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <TextInput
                  label={t("supplyingExportingEntityForPermitHolder")}
                  value={value}
                  onChange={onChange}
                  error={errors?.[name]?.message}
                  required
                  readOnly={isFieldsDisabled}
                />
              )}
            />
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div />
          <div className="flex items-center gap-2">
            {!isFieldsDisabled && (
              <Button type="button" onClick={handleSubmit(onSubmit)}>
                {t("save")}
              </Button>
            )}
            <Button
              onClick={() => setIsAddItemFormVisible(false)}
              className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-purple-primary/10"
            >
              {t("cancel")}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default memo(AddDescriptionOfWarehouseItemCard);
